import { Provider } from "@angular/core";
import { Ref } from "../../shared/providers/ref";
import { AnalysisProvider } from "./components/analysis/analysis-provider";
import { BasemapSwitcherProvider } from "./components/basemap-switcher/basemap-switcher-provider";
import { FooterPanelProvider } from "./components/footer-panel/footer-panel-provider";
import { StdFooterProvider } from "./components/std-footer/std-footer-provider";
import { FullExtentProvider } from "./components/full-extent/full-extent-provider";
import { GraphicProvider } from "./components/graphic/graphic-provider";
import { GraphicService } from "./components/graphic/graphic-service";
import { IdentifyProvider } from "./components/identify/identify-provider";
import { PopupInteractionProvider } from "./components/identify/popup-interaction/popup-interaction-provider";
import { MagneticNorthProvider } from "./components/magnetic-north/magnetic-north-provider";
import { ViewModeProvider } from "./components/map/view-mode-provider";
import { MapProvider } from "./components/map/map-provider";
import { MapGridProvider } from "./components/map-grid/map-grid-provider";
import { MeasureProvider } from "./components/measure/measure-provider";
import { MeasureService } from "./components/measure/measure-service";
import { MousePositionProvider } from "./components/mouse-position/mouse-position-provider";
import { OgcProvider } from "./components/ogc/ogc-provider";
import { OgcService } from "./components/ogc/ogc-service";
import { OverviewMapProvider } from "./components/overview-map/overview-map-provider";
import { PrintProvider } from "./components/print/print-provider";
import { ScaleBarProvider } from "./components/scale-bar/scale-bar-provider";
import { ShareProvider } from "./components/share/share-provider";
import { ZoomInProvider } from "./components/zoom-in/zoom-in-provider";
import { ZoomOutProvider } from "./components/zoom-out/zoom-out-provider";
import { ScaleProvider } from "./services/scale-provider";
import { LayerProvider } from "./shared/layer/layer-provider";
import { UbigeoProvider } from "./shared/layer/ubigeo/ubigeo-provider";
import { BookmarkProvider } from "./shared/layer/bookmark/bookmark-provider";
import { TocProvider } from "./shared/layer/toc/toc-provider";
import { MenuProvider } from "./shared/menu/menu-provider";
import { TourProvider } from "./components/tour/tour-provider";
import { TourRouteProvider } from "./components/tour-route/tour-route-provider";
import { FlightProvider } from "./components/flight/flight-provider";
import { FlightService } from "./components/flight/flight-service";
import { NowcastingProvider } from "./components/nowcasting/nowcasting-provider";
import { WarningProvider } from "./components/warning/warning-provider";
import { TemperatureProvider } from "./components/temperature/temperature-provider";
import { WeatherPointProvider } from "./components/weather/weather-point-provider";
import { NowcastingKpiProvider } from "./components/nowcasting/kpi/kpi-provider";
import { NowcastingKpiService } from "./components/nowcasting/kpi/kpi-service";
import { KpiChartService } from "./components/nowcasting/kpi/kpi-chart-service";
import { HelpGuideProvider } from "./components/help-guide/help-guide-provider";
import { AboutProvider } from "./components/about/about-provider";
import { FireKpiProvider } from "./components/fire/fire-kpi-provider";
import { FireLayerProvider } from "./components/fire/fire-layer-provider";

/**
 * Providers de alcance de sesión — scoped al inyector de la ruta raíz.
 *
 * ─── ¿Por qué NO son singletons root? ────────────────────────────────────
 *
 * Con `providedIn: 'root'`, una instancia vive para siempre aunque el usuario
 * cierre sesión. Al volver a la app tras un logout, el notifier conservaría
 * su estado de la sesión anterior (TOC abierto, panel de identify activo,
 * capas cargadas, effects de ArcGIS huérfanos, etc.).
 *
 * Al listarlos aquí (sin `providedIn`), Angular los escopiea al injector de
 * la ruta: cuando el usuario hace logout y el componente se desmonta,
 * Angular llama `ngOnDestroy()` en cada provider → `dispose()` destruye el
 * notifier, los effects/handles de ArcGIS se liberan y el estado arranca
 * limpio en cada nueva sesión.
 *
 * ─── ¿Por qué Ref está incluido? ─────────────────────────────────────────
 *
 * `Ref` es `providedIn: 'root'`, pero al listarlo aquí Angular crea una
 * instancia adicional scoped al injector de la ruta. Esto permite que los
 * providers escopados resuelvan sus dependencias en el injector correcto
 * (los singletons root no pueden ver providers de ruta sin esta indirección).
 *
 * ─── Regla de oro ────────────────────────────────────────────────────────
 *
 * ❌ NUNCA agregar aquí un provider con `@Injectable({ providedIn: 'root' })`.
 * ✅ Todos los providers de esta lista deben usar `@Injectable()` (sin providedIn).
 *
 * Para providers que deben sobrevivir entre rutas (auth, dark-mode, timeout)
 * usar `@Injectable({ providedIn: 'root' })` y NO listarlos aquí.
 */
export const VIEWER_PROVIDERS: Provider[] = [
  Ref,
  AnalysisProvider,
  BasemapSwitcherProvider,
  FooterPanelProvider,
  FullExtentProvider,
  GraphicProvider,
  GraphicService,
  IdentifyProvider,
  PopupInteractionProvider,
  MagneticNorthProvider,
  MapProvider,
  ViewModeProvider,
  MapGridProvider,
  MeasureProvider,
  MeasureService,
  MousePositionProvider,
  OgcProvider,
  OgcService,
  OverviewMapProvider,
  PrintProvider,
  ScaleBarProvider,
  ShareProvider,
  ZoomInProvider,
  ZoomOutProvider,
  ScaleProvider,
  LayerProvider,
  UbigeoProvider,
  BookmarkProvider,
  TocProvider,
  MenuProvider,
  TourProvider,
  TourRouteProvider,
  FlightProvider,
  FlightService,
  NowcastingProvider,
  NowcastingKpiProvider,
  NowcastingKpiService,
  KpiChartService,
  WarningProvider,
  FireKpiProvider,
  FireLayerProvider,
  TemperatureProvider,
  WeatherPointProvider,
  StdFooterProvider,
  HelpGuideProvider,
  AboutProvider,
];
